import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, LogIn, UserPlus, Shield } from "lucide-react";
import Footer from "@/components/Footer";
import { userTokenStore } from "@/services/sellerTokenStore";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserAuth = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // 'login', 'register', or '2fa'
  const [loading, setLoading] = useState(false);
  
  const [loginForm, setLoginForm] = useState({
    email: "",
    password: ""
  });
  
  const [registerForm, setRegisterForm] = useState({
    email: "",
    full_name: "",
    password: "",
    confirm_password: ""
  });
  
  const [twoFactorForm, setTwoFactorForm] = useState({
    email: "",
    code: "",
    masked_notification_email: ""
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/user/login`, loginForm, { withCredentials: true });
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        setTwoFactorForm({
          email: loginForm.email,
          code: "",
          masked_notification_email: response.data.masked_notification_email || ""
        });
        setMode("2fa");
        toast.success(response.data.message || "Verification code sent!");
      } else {
        // Phase-5 hardening: JWT lives in memory + httpOnly cookie only.
        userTokenStore.setSession(response.data.access_token, response.data.user);
        try { localStorage.setItem("user_email", response.data.user.email); } catch (_) { /* private mode */ }
        toast.success("Login successful!");
        navigate("/user/settings");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirm_password) {
      toast.error("Passwords don't match!");
      return;
    }
    
    if (registerForm.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/user/register`, {
        email: registerForm.email,
        full_name: registerForm.full_name,
        password: registerForm.password
      }, { withCredentials: true });
      
      userTokenStore.setSession(response.data.access_token, response.data.user);
      try { localStorage.setItem("user_email", response.data.user.email); } catch (_) { /* ignore */ }
      toast.success("Registration successful!");
      navigate("/user/settings");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    
    if (!twoFactorForm.code || twoFactorForm.code.length !== 6) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.post(
        `${API}/auth/user/verify-2fa?email=${encodeURIComponent(twoFactorForm.email)}&code=${twoFactorForm.code}`,
        {},
        { withCredentials: true }
      );
      
      userTokenStore.setSession(response.data.access_token, response.data.user);
      try { localStorage.setItem("user_email", response.data.user.email); } catch (_) { /* ignore */ }
      toast.success("Verification successful!");
      navigate("/user/settings");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setLoginForm({ email: "", password: "" });
    setRegisterForm({ email: "", full_name: "", password: "", confirm_password: "" });
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-6xl mx-auto px-6 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">
            Maidly Business
          </h1>
          <button 
            onClick={() => navigate("/")} 
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            ← Back to Home
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="bg-white shadow-md w-full max-w-md">
          <div className="p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 text-center">
              {mode === "login" && "Sign in to your account"}
              {mode === "register" && "Create your account"}
              {mode === "2fa" && "Two-Factor Verification"}
            </h2>
          
            {/* Login Form */}
            {mode === "login" && (
              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    required
                    placeholder="your@email.com"
                    data-testid="login-email"
                    className="w-full px-4 py-3 border border-gray-300 focus:border-blue-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <Input
                    id="login-password"
                    type="password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    required
                    placeholder="Enter your password"
                    data-testid="login-password"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full text-white font-semibold transition-all duration-200"
                  data-testid="login-submit-btn"
                  style={{
                    backgroundColor: '#0073ea',
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#005bb5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#0073ea'}
                  onMouseDown={(e) => e.target.style.backgroundColor = '#C25A10'}
                  onMouseUp={(e) => e.target.style.backgroundColor = '#005bb5'}
                >
                  {loading ? "Logging in..." : "Login"}
                </Button>
                
                <div className="text-center pt-4 border-t">
                  <p className="text-sm text-gray-600">
                    Don't have an account?{" "}
                    <button
                      type="button"
                      onClick={() => switchMode("register")}
                      className="text-blue-700 hover:text-blue-700 font-semibold"
                    >
                      Register here
                    </button>
                  </p>
                </div>
              </form>
            )}

            {/* Register Form */}
            {mode === "register" && (
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="register-name">Full Name</Label>
                  <Input
                    id="register-name"
                    type="text"
                    value={registerForm.full_name}
                    onChange={(e) => setRegisterForm({ ...registerForm, full_name: e.target.value })}
                    required
                    placeholder="John Doe"
                    data-testid="register-name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="register-email">Email Address</Label>
                  <Input
                    id="register-email"
                    type="email"
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                    required
                    placeholder="your@email.com"
                    data-testid="register-email"
                  />
                </div>
                
                <div>
                  <Label htmlFor="register-password">Password</Label>
                  <Input
                    id="register-password"
                    type="password"
                    value={registerForm.password}
                    onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                    required
                    minLength={6}
                    placeholder="At least 6 characters"
                    data-testid="register-password"
                  />
                </div>
                
                <div>
                  <Label htmlFor="register-confirm-password">Confirm Password</Label>
                  <Input
                    id="register-confirm-password"
                    type="password"
                    value={registerForm.confirm_password}
                    onChange={(e) => setRegisterForm({ ...registerForm, confirm_password: e.target.value })}
                    required
                    placeholder="Confirm your password"
                    data-testid="register-confirm-password"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 text-white font-semibold transition-all duration-200"
                  data-testid="register-submit-btn"
                  style={{ backgroundColor: '#0073ea' }}
                  onMouseEnter={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#005bb5')}
                  onMouseLeave={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#0073ea')}
                >
                  {loading ? "Creating account..." : "Register"}
                </button>
                
                <div className="text-center pt-4 border-t">
                  <p className="text-sm text-gray-600">
                    Already have an account?{" "}
                    <button
                      type="button"
                      onClick={() => switchMode("login")}
                      style={{ color: '#0073ea' }}
                      className="font-semibold hover:underline"
                    >
                      Login here
                    </button>
                  </p>
                </div>
              </form>
            )}

            {/* 2FA Verification Form */}
            {mode === "2fa" && (
              <form onSubmit={handleVerify2FA} className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 p-4 mb-4">
                  <div className="flex items-start gap-2">
                    <div style={{ color: '#0073ea' }} className="text-xl">📧</div>
                    <div className="flex-1">
                      <p className="text-sm text-blue-800">
                        A 6-digit verification code has been sent to{" "}
                        <strong>{twoFactorForm.masked_notification_email || "your registered email"}</strong>.
                        Please check your inbox and enter the code below.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="verification-code">Verification Code</Label>
                  <Input
                    id="verification-code"
                    type="text"
                    value={twoFactorForm.code}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setTwoFactorForm({ ...twoFactorForm, code: value });
                    }}
                    required
                    maxLength={6}
                    placeholder="Enter 6-digit code"
                    className="text-center text-2xl tracking-widest font-mono border-gray-300 focus:border-blue-500"
                    data-testid="2fa-code-input"
                  />
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    Code expires in 10 minutes
                  </p>
                </div>
                
                <button
                  type="submit"
                  disabled={loading || twoFactorForm.code.length !== 6}
                  className="w-full py-4 text-white font-semibold transition-all duration-200 disabled:opacity-50"
                  data-testid="2fa-verify-btn"
                  style={{ backgroundColor: '#0073ea' }}
                  onMouseEnter={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#005bb5')}
                  onMouseLeave={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#0073ea')}
                >
                  {loading ? "Verifying..." : "Verify & Login"}
                </button>
                
                <div className="text-center pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => switchMode("login")}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    ← Back to Login
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Need help? Contact us at{" "}
            <a href="mailto:deevelopeer8@gmail.com" style={{ color: '#0073ea' }} className="hover:underline">
              deevelopeer8@gmail.com
            </a>
          </p>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default UserAuth;
